import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useContent } from "@/context/ContentContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
    LogOut,
    Save,
    Plus,
    Trash2,
    Image as ImageIcon,
    ExternalLink,
    Mail,
    Phone,
    Loader2,
} from "lucide-react";

export default function AdminDashboard() {
    const { user, logout } = useAuth();
    const { content, save, refresh } = useContent();
    const navigate = useNavigate();
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [tab, setTab] = useState("general");
    const [contacts, setContacts] = useState([]);
    const [newImage, setNewImage] = useState({ url: "", caption: "" });

    useEffect(() => {
        if (user === false) navigate("/admin/login");
    }, [user, navigate]);

    useEffect(() => {
        if (content) setForm(JSON.parse(JSON.stringify(content)));
    }, [content]);

    useEffect(() => {
        if (tab === "messages" && user) {
            api.get("/contact").then((r) => setContacts(r.data)).catch(() => {});
        }
    }, [tab, user]);

    if (!user || !form) {
        return (
            <div className="min-h-screen bg-tbv-bg flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-tbv-deep" />
            </div>
        );
    }

    const update = (key, value) => setForm({ ...form, [key]: value });
    const updateService = (idx, key, value) => {
        const services = [...form.services];
        services[idx] = { ...services[idx], [key]: value };
        setForm({ ...form, services });
    };
    const updateTestimonial = (idx, key, value) => {
        const testimonials = [...form.testimonials];
        testimonials[idx] = { ...testimonials[idx], [key]: value };
        setForm({ ...form, testimonials });
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await save(form);
            toast.success("Changes saved");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save error");
        } finally {
            setSaving(false);
        }
    };

    const addGalleryItem = async () => {
        if (!newImage.url) return toast.error("Enter an image URL");
        try {
            await api.post("/content/gallery", newImage);
            setNewImage({ url: "", caption: "" });
            await refresh();
            toast.success("Image added");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
    };

    const deleteGalleryItem = async (id) => {
        try {
            await api.delete(`/content/gallery/${id}`);
            await refresh();
            toast.success("Image removed");
        } catch (err) {
            toast.error("Delete error");
        }
    };

    const addTestimonial = () => {
        setForm({
            ...form,
            testimonials: [
                ...form.testimonials,
                { id: `new-${Date.now()}`, name: "New customer", location: "", text: "", rating: 5 },
            ],
        });
    };

    const removeTestimonial = (idx) => {
        setForm({ ...form, testimonials: form.testimonials.filter((_, i) => i !== idx) });
    };

    const deleteContact = async (id) => {
        await api.delete(`/contact/${id}`);
        setContacts(contacts.filter((c) => c.id !== id));
    };

    const tabs = [
        { id: "general", label: "General" },
        { id: "hero", label: "Hero" },
        { id: "services", label: "Services" },
        { id: "gallery", label: "Gallery" },
        { id: "testimonials", label: "Testimonials" },
        { id: "contact", label: "Contact" },
        { id: "messages", label: "Messages" },
    ];

    return (
        <div className="min-h-screen bg-tbv-bg" data-testid="admin-dashboard">
            <header className="bg-tbv-ink text-white sticky top-0 z-40">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        {form.logo_url && (
                            <img src={form.logo_url} alt="logo" className="w-10 h-10 object-contain" />
                        )}
                        <div>
                            <div className="font-condensed text-lg">ADMIN PANEL</div>
                            <div className="text-xs text-white/60">Three Brothers Vivero</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <Link to="/" target="_blank" className="text-sm text-white/70 hover:text-white flex items-center gap-2" data-testid="admin-view-site">
                            <ExternalLink className="w-4 h-4" /> View site
                        </Link>
                        <button
                            onClick={handleSave}
                            disabled={saving}
                            className="btn-primary text-sm disabled:opacity-60"
                            data-testid="admin-save-button"
                        >
                            <Save className="w-4 h-4" />
                            {saving ? "Saving..." : "Save changes"}
                        </button>
                        <button
                            onClick={() => {
                                logout();
                                navigate("/admin/login");
                            }}
                            className="text-sm text-white/70 hover:text-white flex items-center gap-2"
                            data-testid="admin-logout"
                        >
                            <LogOut className="w-4 h-4" /> Sign out
                        </button>
                    </div>
                </div>
                <div className="border-t border-white/10">
                    <div className="max-w-7xl mx-auto px-6 flex gap-1 overflow-x-auto">
                        {tabs.map((t) => (
                            <button
                                key={t.id}
                                onClick={() => setTab(t.id)}
                                data-testid={`admin-tab-${t.id}`}
                                className={`px-4 py-3 text-sm font-medium transition border-b-2 whitespace-nowrap ${
                                    tab === t.id
                                        ? "border-tbv-green text-tbv-green"
                                        : "border-transparent text-white/60 hover:text-white"
                                }`}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 py-10">
                {tab === "general" && (
                    <Section title="General Information">
                        <TextField label="Business name" value={form.business_name} onChange={(v) => update("business_name", v)} testid="field-business-name" />
                        <TextField label="Tagline" value={form.tagline} onChange={(v) => update("tagline", v)} testid="field-tagline" />
                        <TextField label="Logo URL (used in hero, nav and footer)" value={form.logo_url} onChange={(v) => update("logo_url", v)} testid="field-logo-url" />
                        {form.logo_url && (
                            <div className="bg-tbv-ink rounded-xl p-4 inline-block">
                                <img src={form.logo_url} alt="logo preview" className="w-32 h-32 object-contain" />
                            </div>
                        )}
                        <TextArea label="About Us text" value={form.about_text} onChange={(v) => update("about_text", v)} testid="field-about-text" />
                        <TextField label="About Us title" value={form.about_title} onChange={(v) => update("about_title", v)} testid="field-about-title" />
                    </Section>
                )}

                {tab === "hero" && (
                    <Section title="Hero Section">
                        <TextField label="Main title" value={form.hero_title} onChange={(v) => update("hero_title", v)} testid="field-hero-title" />
                        <TextArea label="Subtitle" value={form.hero_subtitle} onChange={(v) => update("hero_subtitle", v)} testid="field-hero-subtitle" />
                        <TextField label="CTA button text" value={form.hero_cta_text} onChange={(v) => update("hero_cta_text", v)} testid="field-hero-cta" />
                        <TextField label="Hero image URL (alternate)" value={form.hero_image_url} onChange={(v) => update("hero_image_url", v)} testid="field-hero-image" />
                    </Section>
                )}

                {tab === "services" && (
                    <Section title="Services">
                        <div className="space-y-6">
                            {form.services.map((s, idx) => (
                                <div key={s.id} className="bg-white border border-tbv-border rounded-2xl p-6 space-y-4" data-testid={`admin-service-${idx}`}>
                                    <div className="font-display font-bold text-lg text-tbv-deep">Service #{idx + 1}</div>
                                    <TextField label="Title" value={s.title} onChange={(v) => updateService(idx, "title", v)} testid={`field-service-title-${idx}`} />
                                    <TextArea label="Description" value={s.description} onChange={(v) => updateService(idx, "description", v)} testid={`field-service-desc-${idx}`} />
                                    <TextField label="Image URL" value={s.image_url} onChange={(v) => updateService(idx, "image_url", v)} testid={`field-service-img-${idx}`} />
                                    <div>
                                        <label className="text-xs uppercase tracking-wider text-tbv-soft font-bold">Icon</label>
                                        <select
                                            value={s.icon}
                                            onChange={(e) => updateService(idx, "icon", e.target.value)}
                                            className="mt-2 w-full bg-tbv-bg border border-tbv-border rounded-xl px-4 py-3"
                                        >
                                            <option value="leaf">Leaf</option>
                                            <option value="trees">Trees</option>
                                            <option value="sprout">Sprout</option>
                                            <option value="axe">Axe</option>
                                        </select>
                                    </div>
                                    {s.image_url && <img src={s.image_url} alt="" className="rounded-xl w-full max-w-sm h-40 object-cover" />}
                                </div>
                            ))}
                        </div>
                    </Section>
                )}

                {tab === "gallery" && (
                    <Section title="Image Gallery">
                        <div className="bg-white border border-tbv-border rounded-2xl p-6 mb-8">
                            <h3 className="font-display font-bold text-lg mb-4">Add new image</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                                <div className="md:col-span-2">
                                    <TextField
                                        label="Image URL"
                                        value={newImage.url}
                                        onChange={(v) => setNewImage({ ...newImage, url: v })}
                                        testid="field-new-image-url"
                                    />
                                </div>
                                <TextField
                                    label="Caption (optional)"
                                    value={newImage.caption}
                                    onChange={(v) => setNewImage({ ...newImage, caption: v })}
                                    testid="field-new-image-caption"
                                />
                                <button onClick={addGalleryItem} className="btn-primary justify-center" data-testid="admin-add-gallery">
                                    <Plus className="w-4 h-4" /> Add
                                </button>
                            </div>
                            <p className="text-xs text-tbv-soft mt-3">
                                Upload your images to a service like Imgur, Cloudinary or Unsplash and paste the public URL here.
                            </p>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            {content?.gallery?.map((g) => (
                                <div key={g.id} className="relative group rounded-xl overflow-hidden border border-tbv-border" data-testid={`admin-gallery-item-${g.id}`}>
                                    <img src={g.url} alt={g.caption} className="w-full h-40 object-cover" />
                                    <button
                                        onClick={() => deleteGalleryItem(g.id)}
                                        className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-lg opacity-0 group-hover:opacity-100 transition"
                                        data-testid={`admin-delete-gallery-${g.id}`}
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                    {g.caption && (
                                        <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs p-2">
                                            {g.caption}
                                        </div>
                                    )}
                                </div>
                            ))}
                            {(!content?.gallery || content.gallery.length === 0) && (
                                <div className="col-span-full text-center py-12 text-tbv-soft border-2 border-dashed border-tbv-border rounded-2xl">
                                    <ImageIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                    No images yet.
                                </div>
                            )}
                        </div>
                    </Section>
                )}

                {tab === "testimonials" && (
                    <Section title="Testimonials">
                        <button onClick={addTestimonial} className="btn-secondary mb-6" data-testid="admin-add-testimonial">
                            <Plus className="w-4 h-4" /> Add testimonial
                        </button>
                        <div className="space-y-4">
                            {form.testimonials.map((t, idx) => (
                                <div key={t.id} className="bg-white border border-tbv-border rounded-2xl p-6 space-y-4" data-testid={`admin-testimonial-${idx}`}>
                                    <div className="flex justify-between items-center">
                                        <div className="font-display font-bold text-tbv-deep">Testimonial #{idx + 1}</div>
                                        <button
                                            onClick={() => removeTestimonial(idx)}
                                            className="text-red-600 hover:bg-red-50 p-2 rounded-lg"
                                            data-testid={`admin-remove-testimonial-${idx}`}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <TextField label="Name" value={t.name} onChange={(v) => updateTestimonial(idx, "name", v)} />
                                        <TextField label="Location" value={t.location} onChange={(v) => updateTestimonial(idx, "location", v)} />
                                    </div>
                                    <TextArea label="Text" value={t.text} onChange={(v) => updateTestimonial(idx, "text", v)} />
                                    <div>
                                        <label className="text-xs uppercase tracking-wider text-tbv-soft font-bold">Stars</label>
                                        <select
                                            value={t.rating}
                                            onChange={(e) => updateTestimonial(idx, "rating", parseInt(e.target.value))}
                                            className="mt-2 w-full bg-tbv-bg border border-tbv-border rounded-xl px-4 py-3"
                                        >
                                            {[5, 4, 3, 2, 1].map((n) => (
                                                <option key={n} value={n}>{n} ★</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Section>
                )}

                {tab === "contact" && (
                    <Section title="Contact Information">
                        <div className="bg-white border border-tbv-border rounded-2xl p-6">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="font-display font-bold text-lg text-tbv-deep">Contact persons (receive calls)</h3>
                                <button
                                    onClick={() => update("contacts", [...(form.contacts || []), { id: `new-${Date.now()}`, name: "", phone: "", role: "Customer Service" }])}
                                    className="btn-secondary text-xs"
                                    data-testid="admin-add-contact-person"
                                >
                                    <Plus className="w-4 h-4" /> Add person
                                </button>
                            </div>
                            <div className="space-y-3">
                                {(form.contacts || []).map((p, idx) => (
                                    <div key={p.id} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end pb-3 border-b border-tbv-border last:border-0" data-testid={`admin-contact-person-${idx}`}>
                                        <div className="md:col-span-4">
                                            <TextField
                                                label="Name"
                                                value={p.name}
                                                onChange={(v) => {
                                                    const arr = [...form.contacts];
                                                    arr[idx] = { ...arr[idx], name: v };
                                                    update("contacts", arr);
                                                }}
                                            />
                                        </div>
                                        <div className="md:col-span-4">
                                            <TextField
                                                label="Phone"
                                                value={p.phone}
                                                onChange={(v) => {
                                                    const arr = [...form.contacts];
                                                    arr[idx] = { ...arr[idx], phone: v };
                                                    update("contacts", arr);
                                                }}
                                            />
                                        </div>
                                        <div className="md:col-span-3">
                                            <TextField
                                                label="Role"
                                                value={p.role}
                                                onChange={(v) => {
                                                    const arr = [...form.contacts];
                                                    arr[idx] = { ...arr[idx], role: v };
                                                    update("contacts", arr);
                                                }}
                                            />
                                        </div>
                                        <button
                                            onClick={() => update("contacts", form.contacts.filter((_, i) => i !== idx))}
                                            className="md:col-span-1 text-red-600 hover:bg-red-50 p-3 rounded-lg flex justify-center"
                                            data-testid={`admin-remove-contact-person-${idx}`}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}
                                {(!form.contacts || form.contacts.length === 0) && (
                                    <div className="text-sm text-tbv-soft py-3">No contact persons. Add at least one.</div>
                                )}
                            </div>
                        </div>

                        <TextField label="Main phone (used in floating button and nav)" value={form.phone} onChange={(v) => update("phone", v)} testid="field-phone" />
                        <TextField label="Email" value={form.email} onChange={(v) => update("email", v)} testid="field-email" />
                        <TextField label="Address" value={form.address} onChange={(v) => update("address", v)} testid="field-address" />
                        <TextField label="Service area" value={form.service_area} onChange={(v) => update("service_area", v)} testid="field-service-area" />
                        <TextField label="Hours" value={form.hours} onChange={(v) => update("hours", v)} testid="field-hours" />
                        <TextField label="Facebook URL" value={form.facebook_url} onChange={(v) => update("facebook_url", v)} testid="field-facebook" />
                        <TextField label="Instagram URL" value={form.instagram_url} onChange={(v) => update("instagram_url", v)} testid="field-instagram" />
                        <TextField label="WhatsApp URL" value={form.whatsapp_url} onChange={(v) => update("whatsapp_url", v)} testid="field-whatsapp" />
                    </Section>
                )}

                {tab === "messages" && (
                    <Section title="Received Messages">
                        {contacts.length === 0 ? (
                            <div className="text-center py-12 text-tbv-soft">No messages yet.</div>
                        ) : (
                            <div className="space-y-3">
                                {contacts.map((c) => (
                                    <div key={c.id} className="bg-white border border-tbv-border rounded-2xl p-6" data-testid={`admin-message-${c.id}`}>
                                        <div className="flex justify-between items-start mb-3">
                                            <div>
                                                <div className="font-display font-bold text-lg">{c.name}</div>
                                                <div className="text-xs text-tbv-soft">
                                                    {new Date(c.created_at).toLocaleString("en-US")}
                                                </div>
                                            </div>
                                            <button onClick={() => deleteContact(c.id)} className="text-red-600 hover:bg-red-50 p-2 rounded-lg">
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                        <div className="flex flex-wrap gap-3 text-sm mb-3">
                                            <a href={`tel:${c.phone}`} className="flex items-center gap-1 text-tbv-deep hover:underline">
                                                <Phone className="w-3 h-3" /> {c.phone}
                                            </a>
                                            {c.email && (
                                                <a href={`mailto:${c.email}`} className="flex items-center gap-1 text-tbv-deep hover:underline">
                                                    <Mail className="w-3 h-3" /> {c.email}
                                                </a>
                                            )}
                                            {c.service && (
                                                <span className="px-2 py-0.5 bg-tbv-green/20 text-tbv-dark text-xs rounded-full">
                                                    {c.service}
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-tbv-ink">{c.message}</p>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Section>
                )}
            </main>
        </div>
    );
}

const Section = ({ title, children }) => (
    <div>
        <h2 className="font-display text-3xl font-bold mb-6">{title}</h2>
        <div className="space-y-5">{children}</div>
    </div>
);

const TextField = ({ label, value, onChange, testid }) => (
    <div>
        <label className="text-xs uppercase tracking-wider text-tbv-soft font-bold">{label}</label>
        <input
            type="text"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            data-testid={testid}
            className="mt-2 w-full bg-white border border-tbv-border rounded-xl px-4 py-3 focus:outline-none focus:border-tbv-deep transition"
        />
    </div>
);

const TextArea = ({ label, value, onChange, testid }) => (
    <div>
        <label className="text-xs uppercase tracking-wider text-tbv-soft font-bold">{label}</label>
        <textarea
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            rows={4}
            data-testid={testid}
            className="mt-2 w-full bg-white border border-tbv-border rounded-xl px-4 py-3 focus:outline-none focus:border-tbv-deep transition resize-none"
        />
    </div>
);
